/**
 * Admin Invitations API Client
 * Handles user invitation management for admin dashboard
 */

import AdminAuthAPI from './auth.js';
import apiClient from '../client.js'; // Keep for public endpoints

const InvitationsAPI = {
  /**
   * Send a new user invitation
   */
  async sendInvitation(invitationData) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest('/admin/invitations/send', {
        method: 'POST',
        body: JSON.stringify(invitationData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invitation');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error sending invitation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get list of invitations with filtering
   */
  async getInvitations(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = queryParams ? `/admin/invitations?${queryParams}` : '/admin/invitations';
      
      const response = await AdminAuthAPI.authenticatedRequest(endpoint);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch invitations');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching invitations:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get invitation details by ID
   */
  async getInvitation(invitationId) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(`/admin/invitations/${invitationId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch invitation');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching invitation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId) {
    try {
      const response = await AdminAuthAPI.authenticatedRequest(`/admin/invitations/${invitationId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel invitation');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Resend an invitation
   */
  async resendInvitation(invitationId, personalMessage = null) {
    try {
      const payload = personalMessage ? { personal_message: personalMessage } : {};
      const response = await AdminAuthAPI.authenticatedRequest(`/admin/invitations/${invitationId}/resend`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to resend invitation');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error resending invitation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get available clinics for invitations
   */
  async getClinics() {
    try {
      const response = await AdminAuthAPI.authenticatedRequest('/admin/clinics');
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch clinics');
      }
      
      const data = await response.json();
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching clinics:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Validate invitation token (public endpoint)
   */
  async validateInvitation(token) {
    try {
      const response = await apiClient.get(`/admin/invitations/validate/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error validating invitation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message
      };
    }
  },

  /**
   * Complete invitation (public endpoint)
   */
  async completeInvitation(completionData) {
    try {
      const response = await apiClient.post('/admin/invitations/complete', completionData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error completing invitation:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message
      };
    }
  }
};

export default InvitationsAPI;